import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { Settings } from "../../pages/Settings";
import { Profile } from "../../pages/Settings/Profile";
import { Security } from "../../pages/Settings/Security";
import { AdminRoute } from "../organisms/AdminRoute";
import { ErrorBoundary } from "../organisms/ErrorBoundary";

// Lazy load pages
const StaffDashboard = lazy(() =>
  import("../../pages/Staff/StaffDashboard").then((m) => ({ default: m.StaffDashboard }))
);
const Orders = lazy(() => import("../../pages/Orders").then((m) => ({ default: m.Orders })));
const OrderDetail = lazy(() =>
  import("../../pages/OrderDetail").then((m) => ({ default: m.OrderDetail }))
);
const Inventory = lazy(() =>
  import("../../pages/Inventory").then((m) => ({ default: m.Inventory }))
);
const InventoryDetail = lazy(() =>
  import("../../pages/InventoryDetail").then((m) => ({ default: m.InventoryDetail }))
);
const ProductionHistory = lazy(() =>
  import("../../pages/ProductionHistory").then((m) => ({ default: m.ProductionHistory }))
);
const Catalog = lazy(() => import("../../pages/Catalog"));
const CatalogDetail = lazy(() => import("../../pages/CatalogDetail"));
const Customers = lazy(() =>
  import("../../pages/Customers").then((m) => ({ default: m.Customers }))
);
const CustomerDetail = lazy(() =>
  import("../../pages/CustomerDetail").then((m) => ({ default: m.CustomerDetail }))
);
const Suppliers = lazy(() =>
  import("../../pages/Suppliers").then((m) => ({ default: m.Suppliers }))
);
const SupplierDetail = lazy(() =>
  import("../../pages/SupplierDetail").then((m) => ({ default: m.SupplierDetail }))
);
const Alerts = lazy(() => import("../../pages/Alerts").then((m) => ({ default: m.Alerts })));

/** Shared fallback for lazy routes */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );
}

/**
 * Staff Operations Routes - Full operational interface for production/inventory staff
 * Routes: /dashboard, /orders, /inventory, /production, /catalog, /customers, /suppliers, /alerts, /settings, /staff
 * Uses MainLayout with limited navigation (no Investments, Reports, Users)
 */
export function StaffOperationsRoutes() {
  return (
    <MainLayout>
      <ErrorBoundary>
        <Routes>
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<RouteFallback />}>
                <StaffDashboard />
              </Suspense>
            }
          />
          <Route
            path="/orders"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Orders />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <OrderDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Inventory />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/inventory/:id"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <InventoryDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/production"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <ProductionHistory />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/catalog"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Catalog />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/catalog/:id"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CatalogDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Customers />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <CustomerDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Suppliers />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/suppliers/:id"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <SupplierDetail />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <AdminRoute allowedRoles={["staff"]}>
                <Suspense fallback={<RouteFallback />}>
                  <Alerts />
                </Suspense>
              </AdminRoute>
            }
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/profile" element={<Profile />} />
          <Route path="/settings/security" element={<Security />} />
          {/* Redirect all other routes (like /investments, /reports, /users, /staff) to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </MainLayout>
  );
}
